import { useEffect, useMemo, useState } from "react";
import ErrorScreen from "../../error/ErrorScreen";
import getDataFromApi from "../../getDataFromApi";
import Loader from "../../Loader";

const customOrder = [
    "Task Name",
    "Dataset",
    "Dataset Version",
    "Total Images",
    "Images Per Task",
    "Image Quality",
    "Annotators",
    "Validators",
    "Annotator Flags",
    "Validator Flags",
    "Upload Annotation Guidelines",
    "Use AI Assist",
    "AI Assist",
    "Labels",
    "Model Details",
    "Foundational Model Details",
    "Experiment Details",
    "Model Labels",
    "Dataset Label",
    "Model Name",
    "Score Threshold",
    "merge_iou",
    "tile_size",
    "tile_overlap",
    "min_area_threshold",
    "max_area_threshold",
    "Parallel AI Assist Pipelines",
    "Skip model prediction with user annotations",
    "Tasks Per Taskset",
    "Taskset Group Name",
    "PreFilters",
    "PostFilters",
    "RandomBrightness",
    "ColorJitter",
    "ChannelShuffle",
    "Filter By",
];

const displayLabels = {
    annotationguidelinefilename: "Upload Annotation Guidelines",
    uploadannotationguidelinesname: "Upload Annotation Guidelines",
};

const metadataKeys = new Set([
    "usernames",
    "userids",
    "datasetname",
    "dsversion",
    "images",
]);

const exactHiddenKeys = new Set([
    "model_name",
    "exp_name",
    "annotation_guideline_filename",
]);

const aiAssistHiddenKeys = new Set([
    "aiassist",
    "modeldetails",
    "experimentdetails",
    "foundationalmodeldetails",
    "skipmodelpredictionwithuserannotations",
    "overlapthreshold",
    "parallelaiassistpipelines",
    "modelname",
    "modellabels",
    "datasetlabel",
    "labels",
    "scorethreshold",
    "labelspromptkey",
    "labelmap",
    "experimentname",
    "expname",
    "mergeiou",
    "tilesize",
    "tileoverlap",
    "minareathreshold",
    "maxareathreshold",
]);

const sectionKeys = new Set([
    "prefilters",
    "postfilters",
    "modeldetails",
    "foundationalmodeldetails",
    "experimentdetails",
]);

const customOrderLookup = new Map(
    customOrder.map((key, index) => [normalizeKey(key), index])
);

function formatKey(key = "") {
    return String(key).replace(/_/g, " ");
}

function normalizeKey(key = "") {
    return formatKey(key)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");
}

function getDisplayLabel(key = "") {
    return displayLabels[normalizeKey(key)] || formatKey(key);
}

function isPlainObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isAiAssistEnabledValue(value) {
    return value === true || value === "Yes" || value === "yes" || value === "true";
}

function isDataPayload(value) {
    return typeof value === "string" && value.trim().startsWith("data:");
}

function getConfiguration(summaryData) {
    if (!summaryData || typeof summaryData !== "object") return null;
    if (isPlainObject(summaryData.configuration)) return summaryData.configuration;
    if (isPlainObject(summaryData.summary)) return summaryData.summary;
    return summaryData;
}

function hasActiveFilters(value) {
    if (!value || typeof value !== "object") return false;

    if (Array.isArray(value)) {
        return value.some(hasActiveFilters);
    }

    if (Object.prototype.hasOwnProperty.call(value, "apply")) {
        return Boolean(value.apply);
    }

    return Object.values(value).some(hasActiveFilters);
}

function getGuidelineFilename(configuration) {
    const filename =
        configuration?.["Upload Annotation Guidelines Name"] ||
        configuration?.annotation_guideline_filename;

    if (filename) return filename;

    const uploadValue = configuration?.["Upload Annotation Guidelines"];
    return uploadValue && !isDataPayload(uploadValue) ? uploadValue : "";
}

function getOrderIndex(key) {
    const normalizedKey = normalizeKey(key);
    const normalizedLabel = normalizeKey(getDisplayLabel(key));

    return (
        customOrderLookup.get(normalizedKey) ??
        customOrderLookup.get(normalizedLabel) ??
        Number.POSITIVE_INFINITY
    );
}

function formatPrimitiveValue(value) {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value === null || value === undefined) return "";
    return String(value);
}

export default function TaskSummary({
    expData,
    query,
    id,
    dataset,
    datasetversion,
    version_id,
}) {
    const [data, setData] = useState();
    const [error, setError] = useState();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!query) {
            setData(undefined);
            setError(undefined);
            setLoading(false);
            return undefined;
        }

        let isMounted = true;
        setLoading(true);
        setError(undefined);

        const url = `${window["ENV"].API_URL}/models/detailed/${id}/configuration${query}`;
        getDataFromApi(url, "get")
            .then((response) => {
                if (!isMounted) return;

                if (response?.detail) {
                    setError(response.detail);
                    return;
                }

                setData(response);
            })
            .catch((e) => {
                if (!isMounted) return;
                setError(e.message);
                console.log(e);
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [id, query]);

    const summaryData = query ? data : expData;
    const configuration = useMemo(() => getConfiguration(summaryData), [summaryData]);
    const displayConfiguration = useMemo(() => {
        if (!configuration) return null;

        const nextConfiguration = { ...configuration };
        const guidelineFilename = getGuidelineFilename(nextConfiguration);

        delete nextConfiguration["Upload Annotation Guidelines"];
        delete nextConfiguration["Upload Annotation Guidelines Name"];
        delete nextConfiguration.annotation_guideline_filename;

        if (guidelineFilename) {
            nextConfiguration["Upload Annotation Guidelines Name"] = guidelineFilename;
        }

        return nextConfiguration;
    }, [configuration]);

    if (loading) return <Loader />;
    if (error || !displayConfiguration) return <ErrorScreen message={"No Data Found"} />;

    const isAiAssistEnabled = isAiAssistEnabledValue(configuration?.["Use AI Assist"]);

    const mapIdsToNames = (ids = []) => {
        const userIds = Array.isArray(configuration?.user_ids) ? configuration.user_ids : [];
        const userNames = Array.isArray(configuration?.user_names) ? configuration.user_names : [];

        return ids.map((userId) => {
            const index = userIds.indexOf(userId);
            return index !== -1 ? userNames[index] : userId;
        });
    };

    const shouldHideEntry = (key, value) => {
        const normalizedKey = normalizeKey(key);

        if (metadataKeys.has(normalizedKey) || exactHiddenKeys.has(key)) return true;
        if (key === "Upload Annotation Guidelines" || isDataPayload(value)) return true;
        if (value === null || value === undefined || value === "") return true;
        if (!isAiAssistEnabled && aiAssistHiddenKeys.has(normalizedKey)) return true;
        if (normalizedKey === "skipmodelpredictionwithuserannotations" && !value) return true;
        if (["prefilters", "postfilters"].includes(normalizedKey) && !hasActiveFilters(value)) {
            return true;
        }
        if (isPlainObject(value) && value.apply === false) return true;

        return false;
    };

    const resolveDatasetValue = (key, value) => {
        if (key === "Dataset") {
            return version_id ? dataset || value : configuration.dataset_name || value;
        }

        if (key === "Dataset Version") {
            return version_id ? datasetversion || value : configuration.ds_version || value;
        }

        return value;
    };

    const renderRow = (key, value, path) => (
        <div className={`task-summary-row task-summary-row--${normalizeKey(key)}`} key={path}>
            <div className="task-summary-row__label">
                <strong>{getDisplayLabel(key)}</strong>
            </div>
            <div className="task-summary-row__value">{value}</div>
        </div>
    );

    const renderLabelList = (labels = []) => {
        const labelItems = labels
            .filter((label) => isPlainObject(label) && label.name)
            .map((label) => ({
                name: label.name,
                prompt: label.prompt,
            }));

        if (labelItems.length === 0) {
            return formatPrimitiveValue(labels.join(", "));
        }

        const hasPrompts = labelItems.some((label) => label.prompt);

        if (!hasPrompts) {
            return (
                <div className="task-summary-chip-list">
                    {labelItems.map((label) => (
                        <span className="task-summary-chip" key={label.name}>
                            {label.name}
                        </span>
                    ))}
                </div>
            );
        }

        return (
            <div className="task-summary-label-list">
                {labelItems.map((label) => (
                    <div className="task-summary-label-list__item" key={label.name}>
                        <span className="task-summary-label-list__name">{label.name}</span>
                        {label.prompt && (
                            <span className="task-summary-label-list__prompt">{label.prompt}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderFilterValue = (key, value) => {
        if (normalizeKey(key) === "filterbylabel" && Array.isArray(value.labels)) {
            return (
                <div className="task-summary-filter-label-list">
                    {value.labels.map((label, index) => (
                        <span className="task-summary-filter-label" key={`${label}-${index}`}>
                            {label}
                        </span>
                    ))}
                </div>
            );
        }

        const filterEntries = Object.entries(value).filter(
            ([filterKey, filterValue]) => filterKey !== "apply" && filterValue !== ""
        );

        if (filterEntries.length === 0) return "Yes";

        return filterEntries
            .map(([filterKey, filterValue]) => {
                const renderedValue = Array.isArray(filterValue)
                    ? filterValue.join(", ")
                    : formatPrimitiveValue(filterValue);

                return `${getDisplayLabel(filterKey)}: ${renderedValue}`;
            })
            .join(", ");
    };

    const renderArrayValue = (key, value, path) => {
        if (["Annotators", "Validators", "Annotator Flags", "Validator Flags"].includes(key)) {
            return mapIdsToNames(value).join(", ");
        }

        if (key === "Labels") {
            return renderLabelList(value);
        }

        if (value.every((item) => !isPlainObject(item))) {
            return value.map(formatPrimitiveValue).join(", ");
        }

        return (
            <div className="task-summary-nested-list">
                {value.map((item, index) => {
                    if (!isPlainObject(item)) {
                        return (
                            <div className="task-summary-nested-list__item" key={`${path}-${index}`}>
                                {formatPrimitiveValue(item)}
                            </div>
                        );
                    }

                    const nestedRows = renderData(item, `${path}-${index}`);
                    if (nestedRows.length === 0) return null;

                    return (
                        <div className="task-summary-nested-list__item" key={`${path}-${index}`}>
                            {nestedRows}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderObjectValue = (key, value, path) => {
        const normalizedKey = normalizeKey(key);

        if (normalizedKey === "imagequality") {
            return (
                value["image quality"] ??
                value.image_quality ??
                Object.values(value).find((item) => item !== undefined && item !== null) ??
                ""
            );
        }

        if (Object.prototype.hasOwnProperty.call(value, "apply")) {
            return value.apply ? renderFilterValue(key, value) : null;
        }

        const nestedRows = renderData(value, path);
        if (nestedRows.length === 0) return null;

        if (sectionKeys.has(normalizedKey)) {
            return (
                <div className="task-summary-section" key={path}>
                    <div className="task-summary-section__title">{getDisplayLabel(key)}</div>
                    <div className="task-summary-section__body">{nestedRows}</div>
                </div>
            );
        }

        return <div className="task-summary-nested">{nestedRows}</div>;
    };

    const renderValue = (key, value, path) => {
        const resolvedValue = resolveDatasetValue(key, value);

        if (Array.isArray(resolvedValue)) {
            return renderArrayValue(key, resolvedValue, path);
        }

        if (isPlainObject(resolvedValue)) {
            return renderObjectValue(key, resolvedValue, path);
        }

        return formatPrimitiveValue(resolvedValue);
    };

    function renderData(item, parentPath = "summary") {
        return Object.entries(item)
            .map(([key, value], index) => ({ key, value, index }))
            .sort((a, b) => {
                const orderA = getOrderIndex(a.key);
                const orderB = getOrderIndex(b.key);

                if (orderA === orderB) return a.index - b.index;
                return orderA - orderB;
            })
            .map(({ key, value }) => {
                if (shouldHideEntry(key, value)) return null;

                const path = `${parentPath}-${key}`;
                const renderedValue = renderValue(key, value, path);

                if (renderedValue === null || renderedValue === "") return null;
                if (sectionKeys.has(normalizeKey(key)) && isPlainObject(value)) {
                    return renderedValue;
                }

                return renderRow(key, renderedValue, path);
            })
            .filter(Boolean);
    }

    const renderedRows = renderData(displayConfiguration);

    return (
        <div className="task-summary-table">
            {renderedRows.length > 0 ? (
                renderedRows
            ) : (
                <p>No configuration data available.</p>
            )}
        </div>
    );
}
